// TAPINTO® Editor Frontend - Asset Store
// Version: 1.0
// Date: 2025-10-01

import { create } from 'zustand';
import { AssetState, Asset, AssetFilters } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface AssetStore extends AssetState {
  fetchAssets: (filters?: AssetFilters) => Promise<void>;
  uploadAsset: (file: File, metadata?: { tags?: string; alt_text?: string }) => Promise<boolean>;
  updateAsset: (assetId: string, updates: { tags?: string; alt_text?: string }) => Promise<boolean>;
  deleteAsset: (assetId: string) => Promise<boolean>;
  searchAssets: (query: string, filters?: { type?: string; tags?: string }) => Promise<void>;
  setSelectedAsset: (asset: Asset | null) => void;
  setUploadProgress: (progress: number) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: AssetFilters) => void;
  clearAssets: () => void;
}

export const useAssetStore = create<AssetStore>((set, get) => ({
  assets: [],
  selectedAsset: null,
  isUploading: false,
  uploadProgress: 0,
  searchQuery: '',
  filters: {},

  fetchAssets: async (filters?: AssetFilters) => {
    try {
      const response = await apiService.getAssets(filters);
      
      if (response.success && response.data) {
        set({ assets: response.data.assets });
      } else {
        toast.error(response.error || 'Failed to fetch assets');
      }
    } catch (error) {
      console.error('Fetch assets error:', error);
      toast.error('Failed to fetch assets');
    }
  },

  uploadAsset: async (file: File, metadata?: { tags?: string; alt_text?: string }) => {
    set({ isUploading: true, uploadProgress: 0 });

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        const current = get().uploadProgress;
        if (current < 90) {
          set({ uploadProgress: current + 10 });
        }
      }, 200);

      const response = await apiService.uploadAsset(file, metadata);
      
      clearInterval(progressInterval);
      set({ uploadProgress: 100 });

      if (response.success && response.data) {
        // Add new asset to the list
        const newAsset: Asset = {
          asset_id: response.data.asset_id,
          account_id: undefined,
          filename: response.data.filename,
          original_filename: response.data.filename,
          path: '',
          type: response.data.type,
          size_bytes: response.data.size_bytes,
          r2_key: '',
          cdn_url: response.data.cdn_url,
          thumbnail_url: response.data.thumbnail_url,
          uploaded_by: '',
          uploaded_at: new Date().toISOString(),
          usage_count: 0,
          brand_compliant: true,
          version: 1,
        };

        set(state => ({
          assets: [newAsset, ...state.assets],
          isUploading: false,
          uploadProgress: 0,
        }));

        toast.success('Asset uploaded successfully');
        return true;
      } else {
        toast.error(response.error || 'Failed to upload asset');
        set({ isUploading: false, uploadProgress: 0 });
        return false;
      }
    } catch (error) {
      console.error('Upload asset error:', error);
      toast.error('Failed to upload asset');
      set({ isUploading: false, uploadProgress: 0 });
      return false;
    }
  },

  updateAsset: async (assetId: string, updates: { tags?: string; alt_text?: string }) => {
    try {
      const response = await apiService.updateAsset(assetId, updates);
      
      if (response.success) {
        set(state => ({
          assets: state.assets.map(asset =>
            asset.asset_id === assetId
              ? { ...asset, ...updates, updated_at: response.data?.updated_at || asset.uploaded_at }
              : asset
          ),
        }));

        toast.success('Asset updated successfully');
        return true;
      } else {
        toast.error(response.error || 'Failed to update asset');
        return false;
      }
    } catch (error) {
      console.error('Update asset error:', error);
      toast.error('Failed to update asset');
      return false;
    }
  },

  deleteAsset: async (assetId: string) => {
    try {
      const response = await apiService.deleteAsset(assetId);
      
      if (response.success) {
        set(state => ({
          assets: state.assets.filter(asset => asset.asset_id !== assetId),
          selectedAsset: state.selectedAsset?.asset_id === assetId ? null : state.selectedAsset,
        }));

        toast.success('Asset deleted successfully');
        return true;
      } else {
        toast.error(response.error || 'Failed to delete asset');
        return false;
      }
    } catch (error) {
      console.error('Delete asset error:', error);
      toast.error('Failed to delete asset');
      return false;
    }
  },

  searchAssets: async (query: string, filters?: { type?: string; tags?: string }) => {
    set({ searchQuery: query });

    try {
      const response = await apiService.searchAssets(query, filters);
      
      if (response.success && response.data) {
        set({ assets: response.data.assets });
      } else {
        toast.error(response.error || 'Search failed');
      }
    } catch (error) {
      console.error('Search assets error:', error);
      toast.error('Search failed');
    }
  },

  setSelectedAsset: (asset: Asset | null) => {
    set({ selectedAsset: asset });
  },

  setUploadProgress: (progress: number) => {
    set({ uploadProgress: progress });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  setFilters: (filters: AssetFilters) => {
    set({ filters });
  },

  clearAssets: () => {
    set({
      assets: [],
      selectedAsset: null,
      isUploading: false,
      uploadProgress: 0,
      searchQuery: '',
      filters: {},
    });
  },
}));
